import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { track, useTrackScreen } from '@/lib/analytics';
import { View, Text, ScrollView, Share, Platform } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import { useRouter } from 'expo-router';
import {
  Inbox,
  Link,
  LayoutGrid,
  LayoutList,
} from 'lucide-react-native';
import Animated, { FadeInUp, FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { useAlert } from '@/components/AlertDialog';
import {
  useSubmissionsUIStore,
  useFilteredSubmissions,
  SUBMISSION_STATUS_FILTERS,
  type SubmissionSortBy,
} from '@/lib/state/submissions-store';
import { useResponsive } from '@/lib/useResponsive';
import { AppLayout } from '@/components/AppLayout';
import AILauncherFAB from '@/components/AILauncherFAB';
import { SearchBar, SortDropdown } from '@/components/SearchFilters';
import { SubmissionFilterSheet } from '@/components/investor/SubmissionFilterSheet';
import { SectionDivider } from '@/components/SectionDivider';
import { SectionHeader, Card } from '@/components/ui';
import { DealflowSkeletonCard, StatsSkeletonCard } from '@/components/Skeleton';
import { hapticMedium, hapticSuccess, hapticLight } from '@/lib/haptics';
import { handleError } from '@/lib/errors';
import { requestMoreInfo, inviteToClaim } from '@/lib/supabase/edge-functions';
import {
  useSubmissions,
  useSubmissionLinks,
  useUpdateSubmissionStatus,
  useShortlistSubmission,
  useConfirmDifferentCompany,
  SubmissionActionError,
  type MatchedClosedCompany,
  type Submission,
} from '@/lib/supabase/submissions';

import { SubmissionCard } from '@/components/investor/SubmissionCard';
import { SubmissionDetailModal } from '@/components/investor/SubmissionDetailModal';
import { SubmissionEmptyState } from '@/components/investor/SubmissionEmptyState';
import { SubmissionActionBar } from '@/components/investor/SubmissionActionBar';

// ============================================================================
// Layout Options
// ============================================================================

type SubmissionLayout = 'list' | 'grid';

const LAYOUT_OPTIONS: { value: SubmissionLayout; icon: typeof LayoutList; label: string }[] = [
  { value: 'list', icon: LayoutList, label: 'List' },
  { value: 'grid', icon: LayoutGrid, label: 'Grid' },
];

// ============================================================================
// Sort Options
// ============================================================================

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'score-high', label: 'Highest Score' },
  { value: 'score-low', label: 'Lowest Score' },
  { value: 'company-az', label: 'Company A-Z' },
  { value: 'company-za', label: 'Company Z-A' },
];

// ============================================================================
// Status Filter Chips
// ============================================================================

// The chip list now lives in `submissions-store` as SUBMISSION_STATUS_FILTERS,
// next to the SubmissionStatus union it has to stay exhaustive against. It used
// to be hardcoded here, which is how a dead "Pending" chip (nothing writes that
// status any more) sat beside a missing "Unscorable" one (a real state with no
// way to filter for it). A compile-time Record + an exhaustiveness test now make
// a chipless status impossible to ship.

// ============================================================================
// Screen
// ============================================================================

export default function SubmissionsInboxScreen() {
  useTrackScreen('SubmissionsInbox');
  const screenTracked = useRef(false);
  useEffect(() => {
    if (screenTracked.current) return;
    screenTracked.current = true;
    track('submissions_inbox_viewed');
  }, []);

  const router = useRouter();
  const { isDesktop } = useResponsive();
  const colors = useThemeColors();

  // Data hooks
  const user = useProfileStore((s) => s.user);
  const { data: submissions = [], isLoading } = useSubmissions(user?.id);
  const { data: links = [] } = useSubmissionLinks(user?.id);
  const submissionLink = links[0] ?? null;

  // UI state from store
  const filteredSubmissions = useFilteredSubmissions(submissions);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const filters = useSubmissionsUIStore((s) => s.filters);
  const updateFilter = useSubmissionsUIStore((s) => s.updateFilter);
  const setStatusGroup = useSubmissionsUIStore((s) => s.setStatusGroup);
  const { error: showError } = useAlert();
  const resetFilters = useSubmissionsUIStore((s) => s.resetFilters);
  const activeFilterCount = useSubmissionsUIStore((s) => s.activeFilterCount);
  const sortBy = useSubmissionsUIStore((s) => s.sortBy);
  const setSortBy = useSubmissionsUIStore((s) => s.setSortBy);
  const selectedIds = useSubmissionsUIStore((s) => s.selectedIds);
  const isSelectionMode = useSubmissionsUIStore((s) => s.isSelectionMode);
  const toggleSelection = useSubmissionsUIStore((s) => s.toggleSelection);
  const selectAll = useSubmissionsUIStore((s) => s.selectAll);
  const enterSelectionMode = useSubmissionsUIStore((s) => s.enterSelectionMode);
  const exitSelectionMode = useSubmissionsUIStore((s) => s.exitSelectionMode);
  const expandedId = useSubmissionsUIStore((s) => s.expandedId);
  const setExpandedId = useSubmissionsUIStore((s) => s.setExpandedId);

  // Local state
  const [layout, setLayout] = useState<SubmissionLayout>('list');
  const [detailSubmission, setDetailSubmission] = useState<Submission | null>(null);

  // Mutations
  const updateStatus = useUpdateSubmissionStatus();
  const shortlistMutation = useShortlistSubmission();
  const confirmDifferent = useConfirmDifferentCompany();
  // 119-07: a shortlist answered 409 matched_closed_company — the deck matches
  // a company that closed on Inverio, and the investor must decide first.
  const [closedConflict, setClosedConflict] = useState<{ submissionId: string; company: MatchedClosedCompany } | null>(null);

  // ============================================================================
  // Computed stats
  // ============================================================================

  const stats = useMemo(() => {
    const scored = submissions.filter((s) => s.ai_score !== null);
    return {
      total: submissions.length,
      scored: scored.length,
      shortlisted: submissions.filter((s) => s.status === 'shortlisted').length,
      avgScore: scored.length > 0
        ? Math.round(scored.reduce((sum, s) => sum + (s.ai_score ?? 0), 0) / scored.length)
        : 0,
    };
  }, [submissions]);

  // ============================================================================
  // Action Handlers
  // ============================================================================

  const handleShortlist = useCallback(async (id: string): Promise<boolean> => {
    hapticSuccess();
    const sub = submissions.find((s) => s.id === id);
    if (!sub || !user?.id) return true;
    try {
      await shortlistMutation.mutateAsync({
        submissionId: id,
        investorId: user.id,
      });
      track('submission_shortlisted', { submissionId: id });
      return true;
    } catch (err) {
      // Expected refusals, answered by code — not reported as bugs.
      if (err instanceof SubmissionActionError && err.kind === 'matched_closed_company') {
        if (err.company) {
          setClosedConflict({ submissionId: id, company: err.company });
          setDetailSubmission(sub); // the decision lives in the detail view
          return false;
        }
      }
      if (err instanceof SubmissionActionError && err.kind !== 'failed') {
        showError("Couldn't shortlist", err.userMessage);
        return true;
      }
      // Surface it. This handler used to swallow the failure in an empty
      // if-block ("handled by the global mutation error handler"), which was the
      // last line of defence on a path that silently lost deals: the submission
      // had already left the inbox and never reached dealflow. The edge function
      // now fails BEFORE the status flip, so the deck is still in the inbox and
      // a retry is safe — but only if the investor is told.
      const { message, isCancelled } = handleError(err, { context: 'SubmissionsInbox.shortlist' });
      if (!isCancelled) showError("Couldn't shortlist", err instanceof SubmissionActionError ? err.userMessage : message);
      return true;
    }
  }, [shortlistMutation, user, submissions, showError]);

  // "Different, continue" on a closed-company match. Held at submission →
  // confirm-different-company (scores it on a NEW private profile, 202); found by
  // a shortlist → the shortlist again, confirmed. Dismiss is handleArchive.
  const handleConfirmDifferentCompany = useCallback(async (id: string, via: 'held' | 'shortlist'): Promise<boolean> => {
    if (!user?.id) return false;
    try {
      if (via === 'held') {
        await confirmDifferent.mutateAsync({ submissionId: id, investorId: user.id });
      } else {
        await shortlistMutation.mutateAsync({ submissionId: id, investorId: user.id, confirmNoConflict: true });
        track('submission_shortlisted', { submissionId: id });
      }
      hapticSuccess();
      setClosedConflict(null);
      return true;
    } catch (err) {
      if (err instanceof SubmissionActionError && err.kind !== 'failed') {
        showError("Couldn't continue", err.userMessage);
        return false;
      }
      const { message, isCancelled } = handleError(err, { context: 'SubmissionsInbox.confirmDifferentCompany' });
      if (!isCancelled) showError("Couldn't continue", err instanceof SubmissionActionError ? err.userMessage : message);
      return false;
    }
  }, [confirmDifferent, shortlistMutation, user, showError]);

  const handleReject = useCallback(async (id: string) => {
    hapticMedium();
    if (!user?.id) return;
    try {
      await updateStatus.mutateAsync({ submissionId: id, status: 'rejected', investorId: user.id });
      track('submission_rejected', { submissionId: id });
    } catch (err) {
      handleError(err, { context: 'SubmissionsInbox.reject' });
    }
  }, [updateStatus, user]);

  const handleArchive = useCallback(async (id: string) => {
    hapticMedium();
    if (!user?.id) return;
    try {
      await updateStatus.mutateAsync({ submissionId: id, status: 'archived', investorId: user.id });
      track('submission_archived', { submissionId: id });
    } catch (err) {
      handleError(err, { context: 'SubmissionsInbox.archive' });
    }
  }, [updateStatus, user]);

  const handleRequestInfo = useCallback(async (id: string, message?: string) => {
    const { error } = await requestMoreInfo({ submissionId: id, message });
    if (error) {
      handleError(new Error(error), { context: 'SubmissionsInbox.requestInfo' });
    } else {
      hapticSuccess();
      track('submission_info_requested', { submissionId: id });
    }
  }, []);

  const handleInviteToClaim = useCallback(async (id: string) => {
    if (!user?.id) return;
    const { error } = await inviteToClaim({ submissionId: id });
    if (error) {
      handleError(new Error(error), { context: 'SubmissionsInbox.inviteToClaim' });
    } else {
      hapticSuccess();
      // The invite is recorded structurally by submission-action (a
      // submission_invites row), which drives the "Invited" tag. We no longer
      // overwrite the submission's scoring status with 'invited'.
      track('submission_claim_invited', { submissionId: id });
    }
  }, [updateStatus, user]);

  // Bulk actions
  const handleBulkShortlist = useCallback(() => {
    selectedIds.forEach((id) => handleShortlist(id));
    exitSelectionMode();
    track('submission_bulk_shortlist', { count: selectedIds.size });
  }, [selectedIds, handleShortlist, exitSelectionMode]);

  const handleBulkReject = useCallback(() => {
    selectedIds.forEach((id) => handleReject(id));
    exitSelectionMode();
    track('submission_bulk_reject', { count: selectedIds.size });
  }, [selectedIds, handleReject, exitSelectionMode]);

  const handleBulkArchive = useCallback(() => {
    selectedIds.forEach((id) => handleArchive(id));
    exitSelectionMode();
    track('submission_bulk_archive', { count: selectedIds.size });
  }, [selectedIds, handleArchive, exitSelectionMode]);

  // Link actions
  const handleCopyLink = useCallback(async () => {
    if (!submissionLink?.url) return;
    hapticLight();
    await Clipboard.setStringAsync(submissionLink.url);
    track('submission_link_copied');
  }, [submissionLink]);

  const handleShareLink = useCallback(async () => {
    if (!submissionLink?.url) return;
    hapticLight();
    if (Platform.OS !== 'web') {
      await Share.share({
        message: `Submit your pitch deck here: ${submissionLink.url}`,
        url: submissionLink.url,
      });
    }
    track('submission_link_shared');
  }, [submissionLink]);

  const handleSetupLink = useCallback(() => {
    router.push('/(investor)/investorprofile');
    track('submission_link_setup_navigated');
  }, [router]);

  // Toggle expanded (for list mode)
  const handleToggleExpanded = useCallback((id: string) => {
    setExpandedId(expandedId === id ? null : id);
  }, [expandedId, setExpandedId]);

  // View detail (opens modal)
  const handleViewDetail = useCallback((id: string) => {
    const sub = submissions.find((s) => s.id === id) ?? null;
    setDetailSubmission(sub);
    if (sub) track('submission_detail_viewed', { submissionId: id });
  }, [submissions]);

  // ============================================================================
  // Derived
  // ============================================================================

  const selectedCount = selectedIds.size;
  const hasData = !isLoading;

  return (
    <AppLayout title="Submissions Inbox" showSearch={false}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* The triage inbox keeps its dashboard entry point and its badge; this
           only gives it a way back and a 44pt action. "Manage submission link"
           is a CONTEXTUAL action for this page, which is what the trailing slot
           is for on a secondary screen — global utilities stay on primary ones. */}
        <ScreenFrame
          title="Submissions Inbox"
          back={{ fallback: '/(investor)/investor-dashboard', label: 'Home' }}
          actions={
            <AnimatedPressable
              onPress={handleSetupLink}
              accessibilityRole="button"
              accessibilityLabel="Manage submission link"
              style={{
                width: 44,
                height: 44,
                borderRadius: 22,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: colors.surface.primary,
              }}
            >
              <Link size={20} color={colors.primary[600]} />
            </AnimatedPressable>
          }
        >
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Search Bar */}
            <Animated.View entering={FadeInUp.delay(150).duration(500)} className="px-5 mb-4">
              <SearchBar
                value={filters.query}
                onChangeText={(text) => updateFilter('query', text)}
                placeholder="Search submissions..."
                activeFilterCount={activeFilterCount}
                // SearchBar only draws the filter button when it has a handler.
                // Without this the score and date filters had no way in at all,
                // and the count badge beside it could never be non-zero.
                onFilterPress={() => setFilterSheetOpen(true)}
              />
            </Animated.View>

            {/* Quick Actions Row */}
            <Animated.View entering={FadeInUp.delay(175).duration(500)} className="px-5 mb-4">
              <View className="flex-row items-center justify-between">
                <View className="flex-row items-center gap-2">
                  <SortDropdown
                    value={sortBy}
                    onChange={(val) => setSortBy(val as SubmissionSortBy)}
                    options={SORT_OPTIONS}
                  />
                </View>
                <View className="flex-row items-center gap-2">
                  {!isSelectionMode ? (
                    <AnimatedPressable
                      onPress={enterSelectionMode}
                      accessibilityRole="button"
                      accessibilityLabel="Enter selection mode"
                      className="px-3 py-2 rounded-lg"
                      style={{
                        backgroundColor: colors.surface.primary,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                      }}
                    >
                      <Text style={{ color: colors.text.secondary }} className="text-sm">Select</Text>
                    </AnimatedPressable>
                  ) : (
                    <>
                      <AnimatedPressable
                        onPress={() => selectAll(filteredSubmissions.map((s) => s.id))}
                        accessibilityRole="button"
                        accessibilityLabel="Select all submissions"
                        className="px-3 py-2 rounded-lg"
                        style={{ backgroundColor: colors.surface.primary }}
                      >
                        <Text style={{ color: colors.text.secondary }} className="text-sm">All</Text>
                      </AnimatedPressable>
                      <AnimatedPressable
                        onPress={exitSelectionMode}
                        accessibilityRole="button"
                        accessibilityLabel="Cancel selection"
                        className="px-3 py-2 rounded-lg"
                        style={{ backgroundColor: colors.surface.secondary }}
                      >
                        <Text style={{ color: colors.text.secondary }} className="text-sm">Cancel</Text>
                      </AnimatedPressable>
                    </>
                  )}
                </View>
              </View>
            </Animated.View>

            {/* Status Filter Chips */}
            <Animated.View entering={FadeInUp.delay(185).duration(500)} className="px-5 mb-4">
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                <View className="flex-row gap-2">
                  {SUBMISSION_STATUS_FILTERS.map((sf) => {
                    // A chip maps to a LIST of statuses (see submissions-store):
                    // "In progress" covers scoring + the legacy pending rows, so
                    // it is active only when its whole group is selected.
                    const isActive = sf.key === 'all'
                      ? filters.statuses.length === 0
                      : sf.statuses.every((s) => filters.statuses.includes(s));

                    return (
                      <AnimatedPressable
                        key={sf.key}
                        onPress={() => {
                          hapticLight();
                          if (sf.key === 'all') {
                            resetFilters();
                          } else {
                            setStatusGroup(sf.statuses);
                          }
                        }}
                        accessibilityRole="button"
                        accessibilityLabel={`Filter by ${sf.label}`}
                        accessibilityState={{ selected: isActive }}
                        className="px-3 py-1.5 rounded-full"
                        style={{
                          backgroundColor: isActive ? colors.primary[50] : colors.surface.primary,
                          borderWidth: 1,
                          borderColor: isActive ? colors.primary[200] : colors.border.light,
                        }}
                      >
                        <Text
                          style={{ color: isActive ? colors.primary[700] : colors.text.secondary }}
                          className="text-sm font-medium"
                        >
                          {sf.label}
                        </Text>
                      </AnimatedPressable>
                    );
                  })}
                </View>
              </ScrollView>
            </Animated.View>

            {/* Section Divider - Overview */}
            <View className="px-5">
              <SectionDivider label="Overview" marginTop={8} marginBottom={12} />
            </View>

            {/* Stats Row */}
            <Animated.View entering={FadeInUp.delay(200).duration(500)} className={`px-5 mb-4 ${isDesktop ? 'pt-2' : ''}`}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                <View className="flex-row gap-3">
                  {!hasData ? (
                    <><StatsSkeletonCard /><StatsSkeletonCard /><StatsSkeletonCard /><StatsSkeletonCard /></>
                  ) : (
                    <>
                      <Card elevated borderWidth={0} className="rounded-2xl w-28" accessibilityLabel={`Total Submissions: ${stats.total}`}>
                        <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">{stats.total}</Text>
                        <Text style={{ color: colors.text.secondary }} className="text-xs">Total</Text>
                      </Card>
                      <Card backgroundColor={colors.primary[50]} borderColor={colors.primary[100]} className="rounded-2xl w-28" accessibilityLabel={`Scored: ${stats.scored}`}>
                        <Text style={{ color: colors.primary[600] }} className="text-2xl font-bold">{stats.scored}</Text>
                        <Text style={{ color: colors.primary[700] }} className="text-xs">Scored</Text>
                      </Card>
                      <Card backgroundColor={colors.traffic.greenLight} borderColor={colors.traffic.greenMuted} className="rounded-2xl w-28" accessibilityLabel={`Shortlisted: ${stats.shortlisted}`}>
                        <Text style={{ color: colors.success }} className="text-2xl font-bold">{stats.shortlisted}</Text>
                        <Text style={{ color: colors.success }} className="text-xs">Shortlisted</Text>
                      </Card>
                      <Card backgroundColor={colors.primary[50]} borderColor={colors.primary[100]} className="rounded-2xl w-28" accessibilityLabel={`Average Score: ${stats.avgScore}`}>
                        <Text style={{ color: colors.primary[600] }} className="text-2xl font-bold">{stats.avgScore}</Text>
                        <Text style={{ color: colors.primary[700] }} className="text-xs">Avg Score</Text>
                      </Card>
                    </>
                  )}
                </View>
              </ScrollView>
            </Animated.View>

            {/* Section Divider - Submissions */}
            <View className="px-5">
              <SectionDivider label="Submissions" marginTop={8} marginBottom={12} />
            </View>

            {/* Submissions List */}
            <Animated.View entering={FadeInUp.delay(300).duration(500)} className="px-5 pb-8">
              <SectionHeader
                title={filters.query ? 'Search Results' : 'All Submissions'}
                className="mb-4"
                right={
                  <View className="flex-row items-center gap-2">
                    <View className="flex-row items-center gap-0.5 p-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                      {LAYOUT_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const isActive = layout === opt.value;
                        return (
                          <AnimatedPressable
                            key={opt.value}
                            onPress={() => setLayout(opt.value)}
                            className="p-2 rounded"
                            style={{ backgroundColor: isActive ? colors.surface.primary : 'transparent' }}
                            accessibilityRole="button"
                            accessibilityLabel={`${opt.label} layout`}
                            accessibilityState={{ selected: isActive }}
                          >
                            <Icon size={18} color={isActive ? colors.primary[600] : colors.text.tertiary} />
                          </AnimatedPressable>
                        );
                      })}
                    </View>
                    <Text style={{ color: colors.text.tertiary }} className="text-sm">{filteredSubmissions.length}</Text>
                  </View>
                }
              />

              <View className={`gap-3 ${layout === 'grid' ? 'flex-row flex-wrap' : ''}`}>
                {/* Skeleton loading */}
                {!hasData && [1, 2, 3, 4, 5].map((i) => (
                  <Animated.View key={`skeleton-${i}`} entering={FadeIn.delay(50 + i * 30).duration(200)}>
                    <DealflowSkeletonCard layout={layout} />
                  </Animated.View>
                ))}

                {/* Submission cards */}
                {hasData && filteredSubmissions.map((submission, index) => (
                  <SubmissionCard
                    key={submission.id}
                    submission={submission}
                    index={index}
                    layout={layout}
                    isExpanded={expandedId === submission.id}
                    onToggleExpanded={handleToggleExpanded}
                    isSelected={selectedIds.has(submission.id)}
                    isSelectionMode={isSelectionMode}
                    onToggleSelection={toggleSelection}
                    onViewDetail={handleViewDetail}
                    onShortlist={handleShortlist}
                    onReject={handleReject}
                    onArchive={handleArchive}
                  />
                ))}

                {/* Empty states */}
                {hasData && submissions.length === 0 && (
                  <SubmissionEmptyState
                    isFilterEmpty={false}
                    totalSubmissions={0}
                    hasQuery={!!filters.query}
                    activeFilterCount={activeFilterCount}
                    onClearSearch={() => updateFilter('query', '')}
                    onResetFilters={resetFilters}
                    onSetupLink={handleSetupLink}
                    onCopyLink={handleCopyLink}
                    onShareLink={handleShareLink}
                    hasSubmissionLink={!!submissionLink}
                  />
                )}

                {hasData && submissions.length > 0 && filteredSubmissions.length === 0 && (
                  <SubmissionEmptyState
                    isFilterEmpty={true}
                    allProcessed={filters.statuses.length === 0 && !filters.query}
                    totalSubmissions={submissions.length}
                    hasQuery={!!filters.query}
                    activeFilterCount={activeFilterCount}
                    onClearSearch={() => updateFilter('query', '')}
                    onResetFilters={resetFilters}
                    onSetupLink={handleSetupLink}
                    onCopyLink={handleCopyLink}
                    onShareLink={handleShareLink}
                    hasSubmissionLink={!!submissionLink}
                  />
                )}
              </View>
            </Animated.View>

            <View className="h-24" />
          </ScrollView>

          {/* Floating bars */}
          {isSelectionMode && selectedCount > 0 && (
            <SubmissionActionBar
              selectedCount={selectedCount}
              onBulkShortlist={handleBulkShortlist}
              onBulkReject={handleBulkReject}
              onBulkArchive={handleBulkArchive}
            />
          )}

          <AILauncherFAB targetRoute="/(investor)/assistant" flagKey="ai_assistant" />
        </ScreenFrame>
      </View>

      {/* Detail Modal */}
      <SubmissionDetailModal
        visible={!!detailSubmission}
        onClose={() => {
          setDetailSubmission(null);
          setClosedConflict(null);
        }}
        submission={detailSubmission}
        closedMatch={
          closedConflict && detailSubmission && closedConflict.submissionId === detailSubmission.id
            ? closedConflict.company
            : null
        }
        onConfirmDifferentCompany={handleConfirmDifferentCompany}
        onShortlist={handleShortlist}
        onReject={handleReject}
        onArchive={handleArchive}
        onRequestInfo={handleRequestInfo}
        onInviteToClaim={handleInviteToClaim}
      />

      {/* Score + date filters. Status is filtered by the chip row above the
          list, deliberately not duplicated here. */}
      <SubmissionFilterSheet
        visible={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        filters={filters}
        onUpdateFilter={updateFilter}
        onReset={resetFilters}
      />
    </AppLayout>
  );
}
